import { z } from 'zod'
import { fetchWithNetworkRetry } from '@/lib/client-fetch'

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
const kopecks = z.number().int().nonnegative()
const signedKopecks = z.number().int()
const count = z.number().int().nonnegative()

const ledgerStateSchema = z.enum(['received', 'expected', 'week_open'])
const rangeSchema = z.object({ from: isoDate, to: isoDate })

const ledgerRowSchema = z.object({
  payoutDate: isoDate,
  deliveryFrom: isoDate,
  deliveryTo: isoDate,
  lagDays: z.number().int(),
  orders: count,
  salesKopecks: kopecks,
  payoutKopecks: kopecks,
  commissionKopecks: signedKopecks.nullable(),
  commissionShareBasisPoints: z.number().int().nullable(),
  state: ledgerStateSchema,
})

const upcomingRowSchema = z.object({
  payoutDate: isoDate,
  deliveryFrom: isoDate,
  deliveryTo: isoDate,
  orders: count,
  salesKopecks: kopecks,
  payoutKopecks: kopecks,
  state: z.enum(['expected', 'week_open']),
})

const reportSchema = z.object({
  // Unknown keys such as a legacy basis or coverage are stripped while the API sheds them.
  range: rangeSchema,
  sales: z.object({ amountKopecks: kopecks, orders: count, averageCheckKopecks: kopecks.nullable() }),
  flow: z.object({
    availability: z.enum(['available', 'partial', 'unavailable']),
    reason: z.string().nullable(),
    deliveryFrom: isoDate.nullable(),
    deliveryTo: isoDate.nullable(),
    payoutFrom: isoDate.nullable().default(null),
    payoutTo: isoDate.nullable().default(null),
    salesKopecks: kopecks.nullable(),
    payoutKopecks: kopecks.nullable(),
    payoutCount: count,
    commissionKopecks: signedKopecks.nullable(),
    commissionShareBasisPoints: z.number().int().nullable(),
  }),
  returns: z.object({ amountKopecks: kopecks, operations: count }),
  // Null when the previous window starts before the first synchronised order.
  previous: z.object({
    from: isoDate,
    to: isoDate,
    amountKopecks: kopecks,
    orders: count,
    returns: z.object({ amountKopecks: kopecks, operations: count }).optional(),
  }).nullable().optional(),
  monthly: z.array(z.object({
    month: z.string().regex(/^\d{4}-\d{2}$/),
    salesKopecks: kopecks,
    orders: count,
    receivedKopecks: kopecks,
    expectedKopecks: kopecks,
  })),
})

const payoutsSchema = z.object({
  coverage: z.object({ paidThrough: isoDate.nullable(), lastPayoutDate: isoDate.nullable() }),
  presets: z.object({
    paid: rangeSchema.nullable(),
    awaiting: rangeSchema.extend({ expectedKopecks: kopecks }).nullable(),
  }),
  upcoming: z.array(upcomingRowSchema),
  notDelivered: z.object({ orders: count, amountKopecks: kopecks }),
  ledger: z.array(ledgerRowSchema),
})

export type LedgerState = z.infer<typeof ledgerStateSchema>
export type MarketMoneyRange = z.infer<typeof rangeSchema>
export type MarketMoneyLedgerRow = z.infer<typeof ledgerRowSchema>
export type MarketMoneyUpcomingRow = z.infer<typeof upcomingRowSchema>
export type MarketMoneyReport = z.infer<typeof reportSchema>
export type MarketMoneyPayouts = z.infer<typeof payoutsSchema>
export type MarketMoneyMonthlyPoint = MarketMoneyReport['monthly'][number]

// The owner has not settled the wording for sales minus payout; change it here only.
export const SALES_PAYOUT_DIFFERENCE: { readonly label: string; readonly shortLabel: string; readonly caption: string | null } = {
  label: 'Комиссия Маркета',
  shortLabel: 'комиссия',
  caption: 'продажи минус выплаты — сюда входят все удержания Маркета',
}

export const MARKET_MONEY_ENDPOINT = '/api/analytics/market-money'
export const MARKET_MONEY_PAYOUTS_ENDPOINT = '/api/analytics/market-money/payouts'

export function marketMoneyPayoutsUrl(): string {
  return MARKET_MONEY_PAYOUTS_ENDPOINT
}

export function marketMoneyUrl(range: MarketMoneyRange): string {
  const params = new URLSearchParams({ from: range.from, to: range.to })
  return `${MARKET_MONEY_ENDPOINT}?${params.toString()}`
}

export function parseMarketMoneyReport(value: unknown): MarketMoneyReport {
  return reportSchema.parse(value)
}

export function parseMarketMoneyPayouts(value: unknown): MarketMoneyPayouts {
  return payoutsSchema.parse(value)
}

async function fetchParsed<T>(url: string, schema: z.ZodType<T>, failure: string, malformed: string): Promise<T> {
  const response = await fetchWithNetworkRetry(url)
  const payload: unknown = await response.json().catch(() => null)
  if (!response.ok) {
    // Only validation errors carry a Russian detail; other statuses may carry framework English.
    const detail = response.status === 422 && payload !== null && typeof payload === 'object' && 'detail' in payload && typeof payload.detail === 'string'
      ? payload.detail
      : failure
    throw new Error(detail)
  }
  const parsed = schema.safeParse(payload)
  if (!parsed.success) throw new Error(malformed)
  return parsed.data
}

export function fetchMarketMoneyReport(range: MarketMoneyRange): Promise<MarketMoneyReport> {
  return fetchParsed(
    marketMoneyUrl(range),
    reportSchema,
    'Не удалось загрузить продажи и выплаты Маркета',
    'Сервер вернул продажи и выплаты Маркета в неожиданном формате.',
  )
}

export function fetchMarketMoneyPayouts(): Promise<MarketMoneyPayouts> {
  return fetchParsed(
    marketMoneyPayoutsUrl(),
    payoutsSchema,
    'Не удалось загрузить выплаты Маркета',
    'Сервер вернул выплаты Маркета в неожиданном формате.',
  )
}

export function formatRubles(kopecksValue: number): string {
  return `${Math.round(kopecksValue / 100).toLocaleString('ru-RU')} ₽`
}

/** Formats a monetary value represented in whole roubles on the API wire. */
export function formatRubleAmount(rublesValue: number | null | undefined): string {
  return rublesValue === null || rublesValue === undefined
    ? '—'
    : formatRubles(Math.round(rublesValue * 100))
}

/** Formats a calculated item price per explicit net gram. */
export function formatRublePerGram(rublesValue: number | null | undefined): string {
  if (rublesValue === null || rublesValue === undefined || !Number.isFinite(rublesValue)) return '—'
  return `${rublesValue.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽/г`
}

export function formatRubleNumber(kopecksValue: number): string {
  return Math.round(kopecksValue / 100).toLocaleString('ru-RU')
}

export function roundedRubles(kopecksValue: number): number {
  return Math.round(kopecksValue / 100)
}

// Displayed as a difference of displayed amounts so the calculation adds up on screen.
export function displayedCommissionRubles(salesKopecks: number, payoutKopecks: number): number {
  return roundedRubles(salesKopecks) - roundedRubles(payoutKopecks)
}

export function formatShare(basisPoints: number, fractionDigits = 1): string {
  return `${(basisPoints / 100).toLocaleString('ru-RU', { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits })}%`
}

export function pluralRu(value: number, forms: readonly [one: string, few: string, many: string]): string {
  const mod10 = value % 10
  const mod100 = value % 100
  const form = mod10 === 1 && mod100 !== 11
    ? forms[0]
    : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? forms[1] : forms[2]
  return `${value.toLocaleString('ru-RU')} ${form}`
}

export function pluralOrders(value: number): string {
  return pluralRu(value, ['заказ', 'заказа', 'заказов'])
}

export function pluralPieces(value: number): string {
  return pluralRu(value, ['изделие', 'изделия', 'изделий'])
}

function dateParts(isoDateValue: string): { year: string; month: string; day: string } {
  return { year: isoDateValue.slice(0, 4), month: isoDateValue.slice(5, 7), day: isoDateValue.slice(8, 10) }
}

export function formatDayMonth(isoDateValue: string): string {
  const { month, day } = dateParts(isoDateValue)
  return `${day}.${month}`
}

export function formatFullDate(isoDateValue: string): string {
  const { year, month, day } = dateParts(isoDateValue)
  return `${day}.${month}.${year}`
}

export function formatPeriod(from: string, to: string): string {
  if (from === to) return formatDayMonth(from)
  if (from.slice(0, 7) === to.slice(0, 7)) return `${dateParts(from).day}–${formatDayMonth(to)}`
  return `${formatDayMonth(from)}–${formatDayMonth(to)}`
}

export function formatRangeWithYear(from: string, to: string): string {
  if (from.slice(0, 4) === to.slice(0, 4)) return `${formatDayMonth(from)} – ${formatFullDate(to)}`
  return `${formatFullDate(from)} – ${formatFullDate(to)}`
}

function utcDate(isoDateValue: string): Date {
  return new Date(`${isoDateValue}T00:00:00Z`)
}

export function addDays(isoDateValue: string, days: number): string {
  const value = utcDate(isoDateValue)
  value.setUTCDate(value.getUTCDate() + days)
  return value.toISOString().slice(0, 10)
}

export function inclusiveDays(from: string, to: string): number {
  return Math.round((utcDate(to).getTime() - utcDate(from).getTime()) / 86_400_000) + 1
}

export function moscowToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now)
}

export const MONTH_NAMES = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'] as const
export const MONTH_NAMES_GENITIVE = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'] as const

export function monthName(monthKey: string): string {
  return MONTH_NAMES[Number(monthKey.slice(5, 7)) - 1] ?? monthKey
}

export function payoutShareBasisPoints(salesKopecks: number, payoutKopecks: number): number | null {
  return salesKopecks > 0 ? Math.round((payoutKopecks / salesKopecks) * 10_000) : null
}
